import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import SuppressedPanel from './SuppressedPanel';
import LoadFailedPanel from './LoadFailedPanel';
import BarList from './GuidedPathBars';
import {
  Panel,
  PanelEmpty,
  CHART_BASE,
  CHART_GRID,
  CHART_COLORS
} from '../../../../component/common/designSystem';
import { WEEKDAY_COLOR } from './guidedPathTheme';

/** A figure that has no meaningful value yet, rather than a misleading zero. */
export const orDash = (value, suffix = '') =>
  value === null || value === undefined ? '-' : `${value}${suffix}`;

/**
 * Load one kind of plan, and decide which of the four states the page is in.
 *
 * THE ORDER OF THESE GATES IS LOAD-BEARING. A failed request is checked before
 * suppression, because rendering the withheld state for a network error would
 * claim a privacy protection that never actually ran — and an admin who reads
 * "withheld to protect participants" has been told something false about their
 * own data.
 *
 * Returns `[stats, node]`. A non-null node is what the page should render
 * instead of itself.
 */
export const usePlanType = (fetcher) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  if (loader) return [null, <Loader key="loader" />];
  if (stats?.error) return [null, <LoadFailedPanel key="failed" />];
  if (stats?.suppressed) {
    return [
      null,
      <SuppressedPanel
        key="withheld"
        minimum={stats.minimum}
        what={stats.label ? `the ${stats.label}` : 'this kind of plan'}
      />
    ];
  }
  return [stats, null];
};

/**
 * Thirty days of doing the work: how many rituals were ticked, and how many
 * distinct people ticked them.
 *
 * BOTH LINES MATTER TOGETHER. A ritual count alone can climb because one
 * committed person started clearing all five every morning, which looks
 * identical to the plan reaching more of the workforce. The people line is what
 * separates them.
 */
export const TrendPanel = ({ trend = [], color = CHART_COLORS.brand }) => {
  const hasData = trend.some((day) => (day.ticked || 0) > 0);

  const options = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: [color, CHART_COLORS.gray],
    stroke: { width: [3, 2], curve: 'smooth', dashArray: [0, 4] },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(trend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  return (
    <Panel title="Last 30 days" subtitle="Rituals ticked, and how many people ticked them">
      {hasData ? (
        <Chart
          options={options}
          series={[
            { name: 'Rituals ticked', data: trend.map((d) => d.ticked || 0) },
            { name: 'People', data: trend.map((d) => d.users || 0) }
          ]}
          type="line"
          height={280}
        />
      ) : (
        <PanelEmpty>
          Plans exist, but nobody has ticked a ritual in the last 30 days. People signed up, so the
          feature is reaching them — it is the daily returning that has not happened.
        </PanelEmpty>
      )}
    </Panel>
  );
};

TrendPanel.propTypes = {
  trend: PropTypes.array,
  color: PropTypes.string
};

/** How many plans were started each day over the last thirty. */
export const StartsPanel = ({ trend = [], color = CHART_COLORS.teal }) => {
  const hasData = trend.some((day) => (day.plans || 0) > 0);

  const options = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [color],
    plotOptions: { bar: { borderRadius: 3, columnWidth: '55%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: lastNDaysLabels(trend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } }
  };

  return (
    <Panel title="Plans started" subtitle="New plans over the last 30 days">
      {hasData ? (
        <Chart
          options={options}
          series={[{ name: 'Plans started', data: trend.map((d) => d.plans || 0) }]}
          type="bar"
          height={260}
        />
      ) : (
        <PanelEmpty>
          No new plans in the last 30 days. Anyone already on one still appears in the engagement
          figures.
        </PanelEmpty>
      )}
    </Panel>
  );
};

StartsPanel.propTypes = {
  trend: PropTypes.array,
  color: PropTypes.string
};

/**
 * Which days people tick their rituals on.
 *
 * WEEKDAY ONLY, NEVER HOUR OF DAY. A day record is keyed by a `DD-MM-YYYY`
 * string with no timezone attached to it, so there is no hour in this data to
 * report even if it were a fair question — and "who does their wellbeing plan
 * at 11pm" is not one this section exists to answer.
 */
export const WeekdayPanel = ({ rows = [] }) => {
  const hasData = rows.some((row) => (row.count || 0) > 0);

  const options = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [WEEKDAY_COLOR],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: rows.map((row) => row.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: {
      y: {
        formatter: (val, { dataPointIndex }) => `${val} days — ${rows[dataPointIndex]?.share}%`
      }
    }
  };

  return (
    <Panel title="By day of the week" subtitle="When the work actually gets done">
      {hasData ? (
        <Chart
          options={options}
          series={[{ name: 'Active days', data: rows.map((row) => row.count || 0) }]}
          type="bar"
          height={260}
        />
      ) : (
        <PanelEmpty>No active days in the last 30 days to spread across the week.</PanelEmpty>
      )}
    </Panel>
  );
};

WeekdayPanel.propTypes = {
  rows: PropTypes.array
};

/** A distribution panel that falls back to a sentence rather than a blank box. */
export const SplitPanel = ({ title, subtitle, rows = [], ramp = [], unit, empty }) => (
  <Panel title={title} subtitle={subtitle}>
    {rows.some((row) => (row.count || 0) > 0) ? (
      <BarList rows={rows} unit={unit} colorFor={(index) => ramp[index] || CHART_COLORS.gray} />
    ) : (
      <PanelEmpty>{empty}</PanelEmpty>
    )}
  </Panel>
);

SplitPanel.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  rows: PropTypes.array,
  ramp: PropTypes.array,
  unit: PropTypes.string,
  empty: PropTypes.node
};

/**
 * The note that explains why there is no completion rate on the page.
 *
 * It is stated on every tab rather than once, because "what share of plans get
 * completed?" is the first question anybody asks of this feature and the answer
 * has to travel with the figures that replace it. Leaving it out would invite
 * somebody to go and compute it from the raw collection, where the field means
 * two different things depending on the kind of plan.
 */
export const CompletionNote = () => (
  <p className="mt-3 text-xs text-shoorah-secondary dark:text-gray-400">
    There is no completion rate on this page on purpose. A plan is flagged complete by a scheduled
    job once its thirty days are up — whether the person finished it or never opened it — and that
    job skips AI plans entirely, so the same flag means two different things depending on the kind
    of plan. Follow-through and active days are used instead: both are counted from what people
    actually ticked, and both mean the same thing for either kind.
  </p>
);

/**
 * The standing note at the foot of every page in the section.
 *
 * Repeated rather than stated once on the overview on purpose: an admin can
 * land on any tab from a link, and the limits of what they are looking at
 * should not depend on which door they came through.
 */
export const GuidedPathFootnote = ({ minimum }) => (
  <p className="mt-6 text-xs text-gray-400 dark:text-gray-500">
    Take-up and engagement only. Nothing anybody wrote or was given is shown here or held behind
    this page — no plan name, no plan description, no ritual, and none of the questionnaire answers
    a plan was built from. Groups smaller than {minimum} are withheld entirely rather than rounded.
  </p>
);

GuidedPathFootnote.propTypes = {
  minimum: PropTypes.number
};
